import { useTranslation } from '../../shared/i18n';
import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Form,
    Select,
    Input,
    Switch,
    Space,
    message,
    Typography,
    Button,
    Tag,
    Spin,
    Divider,
    Tooltip,
    Collapse,
    Segmented,
    Checkbox
} from 'antd';
import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import registrarService from '../services/registrarService';
import TldAdditionalFields from '../pages/domain-registration/components/TldAdditionalFields';
import QuickContactModal from './QuickContactModal';
import { PhoneCountryHint } from '../pages/domain-registration/components/ContactFields';
import { phoneMismatch } from '../pages/domain-registration/components/phoneCountry';
import punycode from 'punycode/';
import { checkoutNotices } from '../constants/registryTerms';

const { Text } = Typography;

const ACTION_LABEL = { register: '注册', transfer: '转入', renew: '续费' };
const ACTION_COLOR = { register: 'green', transfer: 'blue', renew: 'orange' };

const domainOf = (item) => item?.metadata?.domain_name || item?.domain_name || item?.product_name;
const toUnicode = (d) => {
    if (!d || !d.includes('xn--')) return d;
    try { return punycode.toUnicode(d); } catch (e) { return d; }
};
const OTHER_ROLES = [
    { key: 'admin', label: '管理员联系人' },
    { key: 'tech', label: '技术联系人' },
    { key: 'billing', label: '账单联系人' },
];
const isTransfer = (i) => i?.metadata?.action === 'transfer';
// .uk 等（transfer_method=ips_tag）不需要转移码
const needsAuthCode = (i) => isTransfer(i) && (i.metadata?.transfer_method || 'auth_code') === 'auth_code';
const parseNs = (text) => (text || '').split(/[\s,，;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);

/**
 * 域名结算配置（一页完成）：
 * 联系人（可内联新建）→ DNS（默认 Yeap DNS，一个开关）→ 自动续费/隐私 → 转入填转移码 → 后缀附加字段
 * 输出：[{ cart_item_id, domain, contact_id, contact_ids?{admin|tech|billing}, auth_code, nameservers[], auto_renew, whois_privacy, additional_info }]
 * 联系人从通讯录复制到域名自己（之后改通讯录不影响该域名）；默认一个联系人管全部角色。
 * nameservers 为空 = 使用 Yeap DNS（付款注册后后端自动建免费 DNS 区域并切换 NS）。
 */
const DomainCartConfigModal = ({
    visible,
    domainItems = [],
    onCancel,
    onConfirm,
    loading = false,
    serverErrors = []
}) => {
    const { t } = useTranslation();
    const [contacts, setContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [contactId, setContactId] = useState(null);
    const [roleIds, setRoleIds] = useState({});
    const [quickOpen, setQuickOpen] = useState(false);
    // 'yeap' | 'keep'（转入：保留当前 DNS，不发 NS）| 'custom'
    const [dnsMode, setDnsMode] = useState('yeap');
    const [nsText, setNsText] = useState('');
    const [autoRenew, setAutoRenew] = useState(true); // 默认开（可关）
    const [privacy, setPrivacy] = useState(true);
    const [authCodes, setAuthCodes] = useState({});
    // 隐私模式（代理注册）：按域名勾选，只对 item.privacy_mode 有报价的显示
    const [privacyMode, setPrivacyMode] = useState({});
    const [extraInfo, setExtraInfo] = useState({});

    const items = useMemo(() => domainItems.filter(i => i?.metadata?.action !== 'renew'), [domainItems]);
    const contact = useMemo(() => contacts.find(c => c.id === contactId) || null, [contacts, contactId]);

    useEffect(() => {
        if (!visible) return;
        const codes = {};
        items.forEach(i => {
            if (needsAuthCode(i)) codes[i.id] = i.metadata?.auth_code || '';
        });
        setAuthCodes(codes);
        setDnsMode(items.some(i => i.metadata?.action === 'transfer') ? 'keep' : 'yeap');
        setExtraInfo({});
        setRoleIds({});
        setPrivacyMode({});
        loadContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, items]);

    const loadContacts = async (selectId = null) => {
        setLoadingContacts(true);
        try {
            const res = await registrarService.getContactTemplates();
            const list = Array.isArray(res?.data) ? res.data : (res?.data ? Object.values(res.data) : []);
            setContacts(list);
            setContactId(prev => {
                if (selectId) return selectId;
                if (prev && list.some(c => c.id === prev)) return prev;
                return (list.find(c => c.is_default) || list[0] || {}).id ?? null;
            });
        } catch (e) {
            message.error(t('Failed to load contacts'));
        } finally {
            setLoadingContacts(false);
        }
    };

    const contactOptions = contacts.map(c => ({
        value: c.id,
        label: `${c.template_name || `${c.first_name || ''} ${c.last_name || ''}`}${c.email ? `（${c.email}）` : ''}${c.is_default ? t(' · Default') : ''}`
    }));

    // checkout said the dialing code can't be kept for this order → red + blocking until fixed
    const phoneBlockedIds = useMemo(() => new Set((serverErrors || [])
        .filter(e => Object.keys(e?.field_errors || {}).some(k => /(^|\.)phone$/.test(k)))
        .map(e => e.cart_item_id)), [serverErrors]);
    const phoneBlocked = !!contact && phoneBlockedIds.size > 0 && !!phoneMismatch(contact.phone, contact.country);
    const fixContactPhone = async (phone) => {
        try {
            const r = await registrarService.updateContactTemplate({ id: contact.id, phone });
            if (r?.success) loadContacts(contact.id); else message.error(r?.error || t('Failed to save'));
        } catch (e) {
            message.error(e?.error || e?.message || t('Failed to save'));
        }
    };

    const nsList = parseNs(nsText);
    const useYeapDns = dnsMode !== 'custom';
    const hasTransfer = items.some(isTransfer);
    const nsValid = useYeapDns || (nsList.length >= 2 && nsList.every(n => /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(n)));
    const missingAuth = items.filter(i => needsAuthCode(i) && !(authCodes[i.id] || '').trim());
    const ready = !!contactId && nsValid && missingAuth.length === 0 && !phoneBlocked;

    const handleConfirm = () => {
        if (!contactId) return message.warning(t('Select a contact'));
        if (!nsValid) return message.warning(t('Please enter at least 2 valid DNS servers'));
        if (missingAuth.length) return message.warning(t('Please enter the transfer code for {{domain}}', { domain: domainOf(missingAuth[0]) }));
        const roles = Object.fromEntries(Object.entries(roleIds).filter(([, v]) => v && v !== contactId));
        const configs = items.map(item => ({
            cart_item_id: item.id,
            domain: domainOf(item),
            contact_id: contactId,
            ...(Object.keys(roles).length ? { contact_ids: roles } : {}),
            auth_code: needsAuthCode(item) ? (authCodes[item.id] || '').trim() : '',
            // 转入不发 NS = 保留当前 DNS；use_yeap_dns 表示转入完成后切到 Yeap DNS
            nameservers: dnsMode === 'custom' ? nsList : [],
            ...(isTransfer(item) ? { use_yeap_dns: dnsMode === 'yeap' } : {}),
            auto_renew: autoRenew,
            whois_privacy: privacy,
            ...(item.privacy_mode?.available && privacyMode[item.id] ? { privacy_mode: true } : {}),
            additional_info: extraInfo[item.id] || {}
        }));
        onConfirm(configs);
    };

    return (
        <Modal
            title={t("Confirm domain details")}
            open={visible}
            onCancel={onCancel}
            width={720}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={onCancel}>{t('Cancel')}</Button>,
                <Button key="ok" type="primary" loading={loading} disabled={!ready} onClick={handleConfirm}>
                    {t('Place order')}
                </Button>
            ]}
        >
            <Spin spinning={loadingContacts}>
                <Space size={[6, 6]} wrap style={{ marginBottom: 16 }}>
                    {items.map(item => (
                        <Tag key={item.id} color={ACTION_COLOR[item.metadata?.action] || 'default'} style={{ fontSize: 13, padding: '2px 8px' }}>
                            {t(ACTION_LABEL[item.metadata?.action] || '注册')} · {toUnicode(domainOf(item))} · {t('{{years}} years', { years: item.metadata?.years || item.years || 1 })}
                        </Tag>
                    ))}
                </Space>

                <Form layout="vertical" requiredMark={false}>
                    <Form.Item label={t("Contacts")} required tooltip={t("Copied to the domain; later address book edits don't change registered domains")} style={{ marginBottom: 4 }}
                        extra={contact && <PhoneCountryHint phone={contact.phone} country={contact.country} error={phoneBlocked} onFix={fixContactPhone} />}>
                        <Space.Compact style={{ width: '100%', display: 'flex' }}>
                            <Select
                                placeholder={contacts.length ? t('Select a contact') : t('No contacts yet. Create one to continue')}
                                value={contactId}
                                onChange={setContactId}
                                showSearch
                                optionFilterProp="label"
                                style={{ flex: 1, minWidth: 0 }}
                                options={contactOptions}
                            />
                            <Button icon={<PlusOutlined />} onClick={() => setQuickOpen(true)}>{t('Create')}</Button>
                        </Space.Compact>
                    </Form.Item>

                    {contacts.length > 1 && (
                        <Collapse
                            ghost
                            size="small"
                            style={{ marginBottom: 8 }}
                            items={[{
                                key: 'roles',
                                label: <Text type="secondary">{t('Use different details for admin/tech/billing contacts')}</Text>,
                                children: OTHER_ROLES.map(r => (
                                    <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                        <Text style={{ minWidth: 88 }}>{t(r.label)}</Text>
                                        <Select
                                            allowClear
                                            size="small"
                                            style={{ flex: 1, minWidth: 180 }}
                                            placeholder={t('Same as registrant')}
                                            value={roleIds[r.key] || undefined}
                                            onChange={v => setRoleIds(prev => ({ ...prev, [r.key]: v || null }))}
                                            options={contactOptions.filter(o => o.value !== contactId)}
                                        />
                                    </div>
                                ))
                            }]}
                        />
                    )}

                    <Form.Item style={{ marginBottom: useYeapDns ? 12 : 8 }}>
                        {hasTransfer ? (
                            <Space wrap>
                                <Text>{t('DNS')}</Text>
                                <Segmented
                                    size="small"
                                    value={dnsMode}
                                    onChange={setDnsMode}
                                    options={[
                                        { value: 'keep', label: t('Keep current DNS') },
                                        { value: 'yeap', label: t('Use Yeap DNS') },
                                        { value: 'custom', label: t('Custom') },
                                    ]}
                                />
                                <Tooltip title={dnsMode === 'keep' && items.some(i => !isTransfer(i)) ? t('Newly registered domains use Yeap DNS') : t('Automatically create DNS records and set NS when enabled. Disable to enter your own DNS servers.')}>
                                    <QuestionCircleOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)' }} />
                                </Tooltip>
                            </Space>
                        ) : (
                            <Space>
                                <Switch checked={useYeapDns} onChange={(v) => setDnsMode(v ? 'yeap' : 'custom')} />
                                <Text>{t('Use Yeap DNS')}</Text>
                                <Tooltip title={t("Automatically create DNS records and set NS when enabled. Disable to enter your own DNS servers.")}>
                                    <QuestionCircleOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)' }} />
                                </Tooltip>
                            </Space>
                        )}
                    </Form.Item>
                    {!useYeapDns && (
                        <Form.Item
                            validateStatus={nsText && !nsValid ? 'error' : undefined}
                            help={nsText && !nsValid ? t('At least 2 valid hostnames') : undefined}
                        >
                            <Input.TextArea
                                rows={2}
                                value={nsText}
                                onChange={e => setNsText(e.target.value)}
                                placeholder={'ns1.example.com\nns2.example.com'}
                            />
                        </Form.Item>
                    )}

                    <Space size="large" wrap style={{ marginBottom: 8 }}>
                        <Tooltip title={t('Renews automatically from your balance; turn off anytime')}><Space><Switch checked={autoRenew} onChange={setAutoRenew} /><Text>{t('Auto-renewal')}</Text></Space></Tooltip>
                        {!items.every(i => i.privacy_mode?.available && privacyMode[i.id]) && <Space>
                            <Switch checked={privacy} onChange={setPrivacy} />
                            <Text>{t('Privacy protection')}</Text>
                            <Tooltip title={t("Hide personal information in WHOIS (automatically skipped for unsupported extensions)")}>
                                <QuestionCircleOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)' }} />
                            </Tooltip>
                        </Space>}
                    </Space>

                    {items.some(i => i.privacy_mode?.available) && (
                        <div style={{ marginBottom: 8 }}>
                            {items.filter(i => i.privacy_mode?.available).map(item => {
                                const o = item.privacy_mode;
                                const diff = o.diff === null || o.diff === undefined ? null : Number(o.diff);
                                return (
                                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '2px 0' }}>
                                        <Checkbox checked={!!privacyMode[item.id]} onChange={e => setPrivacyMode(prev => ({ ...prev, [item.id]: e.target.checked }))}>
                                            {t('Privacy mode')}{items.length > 1 ? ` · ${toUnicode(domainOf(item))}` : ''}
                                        </Checkbox>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {diff !== null
                                                ? t('{{sign}}{{amount}} {{currency}}', { sign: diff >= 0 ? '+' : '', amount: diff.toFixed(2), currency: o.currency })
                                                : t('{{amount}} {{currency}}', { amount: Number(o.total).toFixed(2), currency: o.currency })}
                                        </Text>
                                        <Tooltip title={t('The domain is registered in our name; your details stay private')}>
                                            <QuestionCircleOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)' }} />
                                        </Tooltip>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {items.some(i => i.metadata?.action === 'transfer') && (
                        <>
                            <Divider style={{ margin: '12px 0' }} />
                            {items.filter(isTransfer).map(item => !needsAuthCode(item) ? (
                                <Form.Item key={item.id} label={toUnicode(domainOf(item))} style={{ marginBottom: 12 }}>
                                    <Text type="secondary">
                                        {item.metadata?.ips_tag
                                            ? t('Change the IPS tag to {{tag}} at your current registrar. No auth code needed', { tag: item.metadata.ips_tag })
                                            : (item.metadata?.transfer_instruction ? t(item.metadata.transfer_instruction) : t('No auth code needed'))}
                                    </Text>
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    key={item.id}
                                    label={t('Transfer code for {{domain}}', { domain: toUnicode(domainOf(item)) })}
                                    required
                                    tooltip={t("Obtain this from your current registrar after unlocking the domain (EPP / Auth Code)")}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input.Password
                                        value={authCodes[item.id] || ''}
                                        onChange={e => setAuthCodes(prev => ({ ...prev, [item.id]: e.target.value }))}
                                        autoComplete="off"
                                        placeholder={t("EPP / Auth code")}
                                    />
                                </Form.Item>
                            ))}
                        </>
                    )}
                </Form>

                {contactId && items.map(item => (
                    <TldAdditionalFields
                        key={`${item.id}-${contactId}`}
                        domain={domainOf(item)}
                        contactId={contactId}
                        contactData={contact}
                        operation={item.metadata?.action === 'transfer' ? 'transfer' : 'register'}
                        registrarId={item.metadata?.registrar_id || null}
                        onChange={values => setExtraInfo(prev => ({ ...prev, [item.id]: values }))}
                    />
                ))}

                {checkoutNotices(items.map(domainOf)).map(n => {
                    const link = <a href={n.url} target="_blank" rel="noopener noreferrer">{t(n.label)}</a>;
                    const [pre, post] = n.sentence.includes('{{registry}}')
                        ? t(n.sentence, { registry: '\u0000' }).split('\u0000')
                        : [`${t(n.sentence)} `, ''];
                    return (
                        <div key={n.key}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{pre}{link}{post}</Text>
                        </div>
                    );
                })}
            </Spin>

            <QuickContactModal
                open={quickOpen}
                isFirst={contacts.length === 0}
                onCancel={() => setQuickOpen(false)}
                onCreated={(c) => {
                    setQuickOpen(false);
                    loadContacts(c?.id || null);
                }}
            />
        </Modal>
    );
};

export default DomainCartConfigModal;
